// frontend/modules/drafts/types/handlers.ts
import React from 'react';

// Importing DraftProcess type directly to avoid circular imports
interface DraftProcess {
  id: string;
  name: string;
  processType: string;
  userId: string;
  status: string;
  processData: string;
  documentIds?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

/**
 * Interface for draft process handlers
 * Each module can implement this interface to handle its specific draft process types
 */
export interface DraftProcessHandler {
  /**
   * Determines if this handler can process a specific draft process type
   * @param processType The process type to check
   * @returns True if this handler can handle the process type
   */
  canHandle: (processType: string) => boolean;
  
  /**
   * Renders the component for resuming a draft process
   * @param draftProcess The draft process to resume
   * @param onComplete Callback function called when the draft is completed
   * @returns React component to render
   */
  renderResumeComponent: (
    draftProcess: DraftProcess, 
    onComplete: () => void
  ) => React.ReactNode;
}

/**
 * Registry for draft process handlers
 * This allows modules to register their handlers during initialization
 */
export class DraftProcessHandlerRegistry {
  private static instance: DraftProcessHandlerRegistry;
  private handlers: DraftProcessHandler[] = [];

  private constructor() {}

  /**
   * Get the singleton instance of the registry
   * @returns The DraftProcessHandlerRegistry instance
   */
  public static getInstance(): DraftProcessHandlerRegistry {
    if (!DraftProcessHandlerRegistry.instance) {
      DraftProcessHandlerRegistry.instance = new DraftProcessHandlerRegistry();
    }
    return DraftProcessHandlerRegistry.instance;
  }

  /**
   * Register a new draft process handler
   * @param handler The handler to register
   */
  public registerHandler(handler: DraftProcessHandler): void {
    this.handlers.push(handler);
  }

  /**
   * Get all registered handlers
   * @returns Array of all registered handlers
   */
  public getHandlers(): DraftProcessHandler[] {
    return this.handlers;
  }

  /**
   * Find a handler for a specific process type
   * @param processType The process type to find a handler for
   * @returns The first handler that can handle the process type or undefined if none found
   */
  public findHandlerForProcessType(processType: string): DraftProcessHandler | undefined {
    return this.handlers.find(handler => handler.canHandle(processType));
  }
}
